<template>
  <Upload
    v-bind="getBindValue"
    v-model:file-list="fileList"
    :class="cardClass"
    :custom-request="customRequest"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <slot>
      <MButton
        v-if="listType === 'text'"
        v-bind="{ ...btnProps, disabled: getBindValue.disabled || btnProps?.disabled }"
        :loading="uploading"
      >
        <slot name="btnText">{{ t(label) }} </slot>
      </MButton>
      <div v-else class="flex flex-col items-center justify-center">
        <PlusOutlined />
        <span class="mt-8px">{{ t(label) }}</span>
      </div>
    </slot>
  </Upload>
</template>

<script setup lang="ts">
  import { PlusOutlined } from '@ant-design/icons-vue';
  import { Upload, UploadFile, UploadProps } from 'ant-design-vue';
  import { cloneDeep, isArray, isBoolean, isObject, isString, omit } from 'lodash-es';
  import { computed, ref, unref, watch } from 'vue';
  import { useAttrs } from '@mts-fe/hooks';
  import { message, useConfirm } from '@mts-fe/utils';
  import { MButton } from '@mts/components/Button';
  import { createImgPreview } from '@mts/components/Preview';
  import { t } from '@mts/hooks/web/useI18n';
  import { getRequestHeaders } from '@mts/utils/auth';
  import { globSetting } from '@mts/utils/env';
  import type { MUploadProps } from '..';

  defineOptions({
    name: 'BatchUpload',
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<MUploadProps>(), {
    size: 'large',
    uploadType: 'other',
    maxCount: 10,
    maxSize: 100,
    listType: 'text',
    showUploadList: false,
    openFileDialogOnClick: true,
    label: 'component.upload.choose',
    deleteConfirm: true,
    allowClear: false,
    multiple: true,
  });
  const attrs = useAttrs();

  const cardClass = computed(() => {
    if (props.listType === 'picture-card') {
      return ['mupload-size-' + props.size];
    }
    return [''];
  });

  /**
   * 上传到服务器的文件夹地址，用于后面区分图片和文件的路径
   * img 上传图片
   * excel 导入Excel
   * temp 临时文件
   * other 其他文件
   */
  const ACCEPT_MAP = {
    img: { bucket: 'pic-file-bucket', accepts: ['png', 'jpg', 'jpeg', 'svg'] },
    excel: { bucket: 'temp-file-bucket', accepts: ['xls', 'xlsx'] },
    temp: { bucket: 'temp-file-bucket', accepts: [] },
    other: { bucket: 'other-file-bucket', accepts: [] },
  };
  const getStringAccept = computed(
    () =>
      props.accept ||
      ACCEPT_MAP[props.uploadType].accepts
        .map((i: string) => (i.indexOf('/') > 0 || i.startsWith('.') ? i : '.' + i))
        .join(','),
  );

  //判断文件类型
  const checkFileType = (file: File, accept: string) => {
    const types = accept
      .split(',')
      .map((i) => (i.startsWith('.') ? i.slice(1) : i))
      .join('|');
    return new RegExp('\\.(' + types + ')$', 'i').test(file.name);
  };

  const fileList = ref<UploadFile[]>([]);
  const pendingFiles = ref<File[]>([]);
  const uploading = ref(false);
  const isUploadChange = ref(false);

  // 批量上传相关状态
  const batchUploading = ref(false);
  const batchUploadQueue = ref<File[]>([]);
  // 用于防抖
  const uploadTimer = ref<number | null>(null);
  const emitTimer = ref<number | null>(null);
  const lastEmittedFiles = ref<File[]>([]);

  const beforeUpload: MUploadProps['beforeUpload'] = (file, _fileList) => {
    if (props.beforeUpload) {
      const result = props.beforeUpload(file, _fileList);
      if (result === false) return false;
    }

    let accept = getStringAccept.value;
    // 设置类型,则判断
    if (accept.length && !checkFileType(file, accept)) {
      message.error!(t('component.upload.acceptUpload', [accept]));
      return false;
    }
    // 设置最大值，则判断
    if (props.maxSize && file.size / 1024 / 1024 >= props.maxSize) {
      message.error(t('component.upload.maxSizeMultiple', [props.maxSize]));
      return false;
    }

    if (props.maxCount === 1 && fileList.value.length >= 1) {
      message.error(t('component.upload.maxNumber', [1]));
      return false;
    }
    // 最大数量
    if (props.maxCount && fileList.value.length >= props.maxCount) {
      message.error(t('component.upload.maxNumber', [props.maxCount]));
      return false;
    }

    // 添加到待上传列表
    const fileWithId = Object.assign(file, {
      uid: `pending-${Date.now()}-${Math.random()}`,
      status: 'waiting',
    });
    pendingFiles.value.push(fileWithId);

    // 使用防抖抛出文件选择事件
    if (emitTimer.value) {
      clearTimeout(emitTimer.value);
    }

    emitTimer.value = window.setTimeout(() => {
      // 只有当文件列表有变化时才抛出事件
      if (hasFileListChanged()) {
        emitFilesSelected();
      }
    }, 100); // 100ms防抖
    // // 批量上传模式，将文件添加到队列中，并阻止默认上传
    // pendingFiles.value.push(file);
    // batchUploadQueue.value.push(file);

    // // 如果启用自动上传，延迟执行上传（确保只调度一次）
    // if (!isUploadScheduled) {
    //   isUploadScheduled = true;

    //   if (uploadTimer.value) {
    //     clearTimeout(uploadTimer.value);
    //   }
    //   uploadTimer.value = window.setTimeout(() => {
    //     executeBatchUpload().finally(() => {
    //       isUploadScheduled = false; // 上传完成后重置标志
    //     });
    //   }, 200);
    // }

    return false; // 阻止默认上传
  };

  // 检查文件列表是否有变化
  const hasFileListChanged = () => {
    if (pendingFiles.value.length !== lastEmittedFiles.value.length) {
      return true;
    }

    // 检查文件名和大小是否有变化
    return pendingFiles.value.some((file, index) => {
      const lastFile = lastEmittedFiles.value[index];
      return !lastFile || file.name !== lastFile.name || file.size !== lastFile.size;
    });
  };

  // 抛出文件选择事件
  const emitFilesSelected = () => {
    const filesToEmit = cloneDeep(pendingFiles.value);
    lastEmittedFiles.value = filesToEmit;

    emit('filesSelected', {
      files: filesToEmit,
      totalCount: filesToEmit.length,
      totalSize: filesToEmit.reduce((total, file) => total + file.size, 0),
    });
  };

  // 执行批量上传
  const executeBatchUpload = async (data) => {
    pendingFiles.value = [...data];
    batchUploadQueue.value = [...data];
    if (batchUploadQueue.value.length === 0) {
      return;
    }
    // 创建当前上传的文件副本，避免后续修改影响
    const filesToUpload = [...batchUploadQueue.value];

    batchUploading.value = true;
    uploading.value = true;

    try {
      const formData = new FormData();
      filesToUpload.forEach((file, _index) => {
        formData.append('file', file);
      });
      // 添加额外数据
      const uploadData = {
        bucketName: ACCEPT_MAP[props.uploadType].bucket,
        makeFileNameEndWithTimestamp: true,
        randomFileName: false,
        ...props.data,
      };

      Object.keys(uploadData).forEach((key) => {
        formData.append(key, uploadData[key]);
      });
      // 使用fetch发送请求
      const headers = getRequestHeaders({ url: action.value });
      const response = await fetch(action.value, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      const result = await response.json();
      message.success(t('ML2508270001'));
      emit('successResponse', result?.data);

      // 处理上传成功的文件
      const uploadedFiles = result.data || [];
      // 更新文件列表状态
      uploadedFiles.forEach((fileData: any, index: number) => {
        const file = filesToUpload[index];
        if (file) {
          // 更新对应的文件状态
          fileList.value = fileList.value.map((f) => {
            if (f.name === file.name && f.status === 'uploading') {
              return {
                ...f,
                status: 'done',
                url: fileData.uploadKey || fileData.url,
                response: { data: fileData },
                percent: 100,
              };
            }
            return f;
          });

          emit('uploadSuccess', {
            fileName: file.name,
            fileUrl: fileData.uploadKey || fileData.url,
            file,
          });
        }
      });

      // 从队列中移除已上传的文件
      batchUploadQueue.value = batchUploadQueue.value.filter(
        (file) => !filesToUpload.some((uploadedFile) => uploadedFile.name === file.name),
      );

      pendingFiles.value = pendingFiles.value.filter(
        (file) => !filesToUpload.some((uploadedFile) => uploadedFile.name === file.name),
      );

      // 触发完成事件
      changeValue();
      emit('allSuccess', {
        list: fileList.value.filter((f) => f.status === 'done').map((f) => f.url || ''),
        files: fileList.value
          .filter((f) => f.status === 'done')
          .map((f) => ({
            fileName: f.name,
            fileUrl: f.url || f.response?.data?.uploadKey,
          })),
      });
      return result?.data;
    } catch (error) {
      // 上传失败，更新文件状态
      fileList.value = fileList.value.map((file) => {
        if (filesToUpload.some((f) => f.name === file.name)) {
          return { ...file, status: 'error', percent: 0 };
        }
        return file;
      });

      message.error(t('component.upload.uploadError'));
    } finally {
      batchUploading.value = false;
      uploading.value = false;
    }
  };

  const emit = defineEmits([
    'uploadSuccess',
    'uploading',
    'update:value',
    'change',
    'done',
    'allSuccess',
    'batchUpload',
    'successResponse',
    'filesSelected', // 新增：文件选择事件
  ]);

  // 自定义上传处理
  const customRequest = async () => {};

  // 暴露批量上传方法给父组件
  // const uploadBatch = () => {
  //   return executeBatchUpload();
  // };

  const changeValue = (files = fileList.value) => {
    const list = files.map((item) => item.url);
    const value = props.maxCount > 1 ? list : list.length > 0 ? list[0] : '';
    // 如果手动上传，就不需要监听value的改变了
    isUploadChange.value = true;
    emit('update:value', value);
    emit('change', value);
  };

  watch(
    () => props.value,
    (v) => {
      // 如果手动上传，就不需要监听value的改变了
      if (isUploadChange.value === true) {
        return;
      }
      if (v) {
        let value: string[] = [];
        if (isArray(v)) {
          value = v;
        } else {
          value.push(v);
        }
        fileList.value = value
          .filter((item) => !!item)
          .map((item, i) => {
            if (item && isString(item)) {
              return {
                uid: -i + '',
                name: decodeURIComponent(item.substring(item.lastIndexOf('/') + 1)),
                status: 'done',
                url: item,
              };
            } else if (item && isObject(item)) {
              return item;
            } else {
              return;
            }
          }) as UploadFile[];
      }
    },
    { immediate: true, deep: true },
  );

  const action = computed(
    () => globSetting.apiUrl + (props.action || '/ims-system/system/file/batchUpload'),
  );

  const getBindValue = computed(() => {
    return {
      ...unref(attrs),
      ...omit(props, ['btnProps', 'uploadType', 'label', 'maxSize', 'batchUpload', 'autoUpload']),
      action: action.value,
      headers: getRequestHeaders({ url: action.value }),
      accept: getStringAccept.value,
      data: {
        bucketName: ACCEPT_MAP[props.uploadType].bucket,
        makeFileNameEndWithTimestamp: true,
        randomFileName: false,
        ...props.data,
      },
      showUploadList:
        isBoolean(props.showUploadList) && props.showUploadList
          ? { showDownloadIcon: true }
          : props.showUploadList,
      beforeUpload,
      onChange: undefined,
      onPreview: (file) => {
        if (checkFileType(file as unknown as File, ACCEPT_MAP.img.accepts.join(','))) {
          file.url && createImgPreview({ imageList: [file.url] });
        } else {
          file.url && window.open(file.url, '_blank');
        }
      },
      onRemove: (file) => {
        if (props.deleteConfirm) {
          return new Promise((resolve) => {
            useConfirm({
              title: t('common.reminder'),
              iconType: 'warning',
              content: t('common.deleteConfirm'),
              onOk: () => {
                fileList.value = fileList.value.filter((item) => item.url !== file.url);
                const index = pendingFiles.value.findIndex((f) => f.name === file.name);
                if (index !== -1) {
                  pendingFiles.value.splice(index, 1);
                }
                const batchIndex = batchUploadQueue.value.findIndex((f) => f.name === file.name);
                if (batchIndex !== -1) {
                  batchUploadQueue.value.splice(batchIndex, 1);
                }
                changeValue();
                resolve(true);
              },
            });
          });
        } else {
          fileList.value = fileList.value.filter((item) => item.url !== file.url);
          const index = pendingFiles.value.findIndex((f) => f.name === file.name);
          if (index !== -1) {
            pendingFiles.value.splice(index, 1);
          }
          const batchIndex = batchUploadQueue.value.findIndex((f) => f.name === file.name);
          if (batchIndex !== -1) {
            batchUploadQueue.value.splice(batchIndex, 1);
          }
          changeValue();
        }
      },
    } as UploadProps;
  });

  const clear = () => {
    fileList.value = [];
    pendingFiles.value = [];
    batchUploadQueue.value = [];
    if (uploadTimer.value) {
      clearTimeout(uploadTimer.value);
      uploadTimer.value = null;
    }
    changeValue();
  };

  defineExpose({
    clear,
    // uploadBatch,
    getPendingFiles: () => pendingFiles.value,
    getBatchQueue: () => batchUploadQueue.value,
    executeBatchUpload,
  });
</script>

<style lang="less" scoped>
  :deep(.ant-upload-disabled) {
    display: none !important;
  }

  @size: {
    small: 100px;
    middle: 150px;
    large: 200px;
  };

  each(@size,{
    .mupload-size-@{key} {
      :deep(.ant-upload-list-item-container){
        width: @value !important;
        height: @value !important;
      }

      :deep(.ant-upload){
        width: @value !important;
        height:@value !important;
      }

      :deep(.ant-btn.ant-btn-sm.ant-btn-icon-only){
        width: (@value/4) !important;
        height: (@value/4) !important;
      }

      :deep(span.anticon){
        font-size: (@value / 100 * 14) !important;
        width: (@value / 100 * 14) !important;
      }

      :deep(.anticon-eye){
          margin: 0 (@value / 100 * 4) !important;
      }
    }
  });
</style>
